import React, { useEffect } from 'react';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo from '../../../../components/SelectTwo';
import { PackingOrderDataForm } from '../../../../app/models/PackingOrder';

type SelectOpt = { id: number | string; name: string };

interface Props {
  packingOrder: PackingOrderDataForm;
  setPackingOrder: (data: PackingOrderDataForm) => void;
  societies?: SelectOpt[];
  statusChoices?: SelectOpt[];
  centers?: SelectOpt[];
  warehouses?: SelectOpt[];
  errorFields?: any;
  /** En creación el estado lo maneja el sistema (siempre "Creado"), no se muestra el campo */
  hideStatus?: boolean;
  /** Solo Sociedad y Fecha de programa (ej: modal de creación) */
  onlySocietyAndProgramDate?: boolean;
  /** Callbacks opcionales para cargar centros/bodegas en creación */
  onSocietyChange?: (societyId?: number) => void;
  onCenterChange?: (centerId?: number) => void;
}

const PackingOrderForm = ({
  packingOrder,
  setPackingOrder,
  societies = [],
  statusChoices = [],
  centers = [],
  warehouses = [],
  errorFields,
  hideStatus = false,
  onlySocietyAndProgramDate = false,
  onSocietyChange,
  onCenterChange,
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

  useEffect(() => {
    setErrors(errorFields);
  }, [errorFields, setErrors]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setPackingOrder({ ...packingOrder, [name]: value });
  };

  const handleSelectTwo = (name: keyof PackingOrderDataForm, value: number | string | null) => {
    // Si cambia sociedad, limpiamos centro y almacén
    if (name === 'society_id') {
      const numeric = value != null ? Number(value) : undefined;
      setPackingOrder({
        ...packingOrder,
        society_id: (numeric ?? undefined) as any,
        center_id: undefined,
        warehouse_id: undefined,
      });
      onSocietyChange?.(numeric);
      return;
    }
    // Si cambia centro, limpiamos almacén
    if (name === 'center_id') {
      const numeric = value != null ? Number(value) : undefined;
      setPackingOrder({
        ...packingOrder,
        center_id: (numeric ?? undefined) as any,
        warehouse_id: undefined,
      });
      onCenterChange?.(numeric);
      return;
    }
    setPackingOrder({ ...packingOrder, [name]: (value ?? undefined) as any });
  };

  const societyOptions = societies.map((s) => ({ value: s.id, label: String(s.name) }));
  const statusOptions = statusChoices.map((s) => ({ value: s.id, label: String(s.name) }));
  const centerOptions = centers.map((c) => ({ value: c.id, label: String(c.name) }));
  const warehouseOptions = warehouses.map((w) => ({ value: w.id, label: String(w.name) }));

  return (
    <>
      {/* Fila 1: Sociedad, Fecha de Programa */}
      {onlySocietyAndProgramDate ? (
        <>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold" htmlFor="society_id">
                Sociedad
              </label>
              <SelectTwo
                name="society_id"
                id="society_id"
                inputValue={packingOrder.society_id ?? null}
                options={societyOptions}
                hasError={fieldHasError('society_id') !== ''}
                onChange={(v: any) => handleSelectTwo('society_id', v?.value ?? null)}
                placeholder="Seleccione sociedad"
                onFocus={() => onFocusRemove('society_id')}
              />
              <div
                className={`invalid-feedback ${fieldHasError('society_id') !== '' ? 'd-block' : ''}`}
                style={{ display: 'flex' }}
              >
                {fieldErrorMessage('society_id')}
              </div>
            </div>
          </div>
          {/* Centro y Almacén */}
          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold" htmlFor="center_id">
                Centro
              </label>
              <SelectTwo
                name="center_id"
                id="center_id"
                inputValue={packingOrder.center_id ?? null}
                options={centerOptions}
                hasError={fieldHasError('center_id') !== ''}
                onChange={(v: any) => handleSelectTwo('center_id', v?.value ?? null)}
                placeholder="Seleccione centro"
                onFocus={() => onFocusRemove('center_id')}
              />
              <div
                className={`invalid-feedback ${fieldHasError('center_id') !== '' ? 'd-block' : ''}`}
                style={{ display: 'flex' }}
              >
                {fieldErrorMessage('center_id')}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold" htmlFor="warehouse_id">
                Almacén
              </label>
              <SelectTwo
                name="warehouse_id"
                id="warehouse_id"
                inputValue={packingOrder.warehouse_id ?? null}
                options={warehouseOptions}
                hasError={fieldHasError('warehouse_id') !== ''}
                onChange={(v: any) => handleSelectTwo('warehouse_id', v?.value ?? null)}
                placeholder="Seleccione almacén"
                onFocus={() => onFocusRemove('warehouse_id')}
              />
              <div
                className={`invalid-feedback ${fieldHasError('warehouse_id') !== '' ? 'd-block' : ''}`}
                style={{ display: 'flex' }}
              >
                {fieldErrorMessage('warehouse_id')}
              </div>
            </div>
          </div>
          <div className="row g-3 mt-1 align-items-end">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold" htmlFor="program_date">
                {packingOrder.is_periodic ? 'Fecha de inicio' : 'Fecha de programa'}
              </label>
              <input
                type="date"
                name="program_date"
                id="program_date"
                className={`form-control ${fieldHasError('program_date')}`}
                value={packingOrder.program_date ?? ''}
                onChange={handleChange}
                onFocus={() => onFocusRemove('program_date')}
              />
              <div
                className={`invalid-feedback ${fieldHasError('program_date') !== '' ? 'd-block' : ''}`}
                style={{ display: 'flex' }}
              >
                {fieldErrorMessage('program_date')}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Tipo de orden</label>
              <div className="d-flex gap-3">
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_periodic"
                    checked={!packingOrder.is_periodic}
                    onChange={() =>
                      setPackingOrder({
                        ...packingOrder,
                        is_periodic: false,
                        recurrence_days: [],
                        end_date: null as any,
                      })
                    }
                  />
                  Orden única
                </label>
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_periodic"
                    checked={!!packingOrder.is_periodic}
                    onChange={() =>
                      setPackingOrder({
                        ...packingOrder,
                        is_periodic: true,
                        recurrence_days: packingOrder.recurrence_days ?? [],
                      })
                    }
                  />
                  Periódica
                </label>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="mb-2 row">
          <div className="col-md-6">
            <label className="col-form-label" htmlFor="society_id">
              Sociedad
            </label>
            <SelectTwo
              name="society_id"
              id="society_id"
              inputValue={packingOrder.society_id ?? null}
              options={societyOptions}
              hasError={fieldHasError('society_id') !== ''}
              onChange={(v: any) => handleSelectTwo('society_id', v?.value ?? null)}
              placeholder="Seleccione una sociedad"
              onFocus={() => onFocusRemove('society_id')}
            />
            <div className="invalid-feedback" style={{ display: 'flex' }}>
              {fieldErrorMessage('society_id')}
            </div>
          </div>
          <div className="col-md-6">
            <label className="col-form-label" htmlFor="program_date">
              Fecha de programa
            </label>
            <input
              type="date"
              name="program_date"
              id="program_date"
              className={`form-control ${fieldHasError('program_date')}`}
              value={packingOrder.program_date ?? ''}
              onChange={handleChange}
              onFocus={() => onFocusRemove('program_date')}
            />
            <div className="invalid-feedback" style={{ display: 'flex' }}>
              {fieldErrorMessage('program_date')}
            </div>
          </div>
        </div>
      )}

      {/* Configuración de periodicidad (solo cuando es periódica, en creación) */}
      {onlySocietyAndProgramDate && packingOrder.is_periodic && (
        <div className="row g-3 mt-3">
          <div className="col-12">
            <label className="form-label fw-semibold">Días de la semana</label>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {[
                { v: 0, label: 'L' },
                { v: 1, label: 'M' },
                { v: 2, label: 'X' },
                { v: 3, label: 'J' },
                { v: 4, label: 'V' },
                { v: 5, label: 'S' },
                { v: 6, label: 'D' },
              ].map(({ v, label }) => {
                const days = packingOrder.recurrence_days ?? [];
                const checked = days.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-outline-secondary'}`}
                    title={['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'][v]}
                    onClick={() => {
                      const next = checked ? days.filter((d) => d !== v) : [...days, v].sort((a, b) => a - b);
                      setPackingOrder({ ...packingOrder, recurrence_days: next });
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <small className="text-muted">
              L=Lunes, M=Martes, X=Miércoles, J=Jueves, V=Viernes, S=Sábado, D=Domingo
            </small>
          </div>
          <div className="col-12 col-md-6">
            <label htmlFor="end_date" className="form-label fw-semibold">
              Hasta el
            </label>
            <input
              type="date"
              name="end_date"
              id="end_date"
              className={`form-control ${fieldHasError('end_date')}`}
              value={packingOrder.end_date ?? ''}
              onChange={handleChange}
              onFocus={() => onFocusRemove('end_date')}
            />
            <div
              className={`invalid-feedback ${fieldHasError('end_date') !== '' ? 'd-block' : ''}`}
              style={{ display: 'flex' }}
            >
              {fieldErrorMessage('end_date')}
            </div>
          </div>
        </div>
      )}

      {/* Fila 2: Fecha de Inicio, Estado, Fecha Fin */}
      {!onlySocietyAndProgramDate && (
        <div className="mb-2 row">
          <div className={hideStatus ? 'col-md-6' : 'col-md-4'}>
            <label className="col-form-label">Fecha de inicio</label>
            <input
              type="date"
              name="start_date"
              className={`form-control ${fieldHasError('start_date')}`}
              value={packingOrder.start_date ?? ''}
              onChange={handleChange}
              onFocus={() => onFocusRemove('start_date')}
            />
            <div className="invalid-feedback" style={{ display: 'flex' }}>
              {fieldErrorMessage('start_date')}
            </div>
          </div>
          {!hideStatus && (
            <div className="col-md-4">
              <label className="col-form-label">Estado</label>
              <SelectTwo
                name="status"
                id="estado"
                inputValue={packingOrder.status ?? null}
                options={statusOptions}
                hasError={fieldHasError('status') !== ''}
                onChange={(v: any) => handleSelectTwo('status', v?.value ?? null)}
                placeholder="Seleccione estado"
                onFocus={() => onFocusRemove('status')}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>
                {fieldErrorMessage('status')}
              </div>
            </div>
          )}
          <div className={hideStatus ? 'col-md-6' : 'col-md-4'}>
            <label className="col-form-label">Fecha fin</label>
            <input
              type="date"
              name="end_date"
              className={`form-control ${fieldHasError('end_date')}`}
              value={packingOrder.end_date ?? ''}
              onChange={handleChange}
              onFocus={() => onFocusRemove('end_date')}
            />
            <div className="invalid-feedback" style={{ display: 'flex' }}>
              {fieldErrorMessage('end_date')}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PackingOrderForm;
